import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { User as UserIcon, Building2, Lock } from 'lucide-react';

interface MembershipForm {
  full_name: string;
  gender: string;
  date_of_birth: string;
}

interface CompanyForm {
  company_name: string;
  company_representative: string;
  company_register_number: string;
}

const emptyMembershipForm: MembershipForm = { full_name: '', gender: '', date_of_birth: '' };
const emptyCompanyForm: CompanyForm = { company_name: '', company_representative: '', company_register_number: '' };

const ProfileDetails: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [membershipForm, setMembershipForm] = useState<MembershipForm>(emptyMembershipForm);
  const [companyForm, setCompanyForm] = useState<CompanyForm>(emptyCompanyForm);
  const [savingMembership, setSavingMembership] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => {
    if (!user) return;
    axios.get(API.getUserProfile(user.id), { headers: authHeaders() })
      .then((res) => {
        setMembershipForm({
          full_name: res.data.full_name || '',
          gender: res.data.gender || '',
          date_of_birth: res.data.date_of_birth || '',
        });
        setCompanyForm({
          company_name: res.data.company_name || '',
          company_representative: res.data.company_representative || '',
          company_register_number: res.data.company_register_number || '',
        });
      })
      .catch(() => setError('Failed to load your profile.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveMembership = async () => {
    if (!user) return;
    setSavingMembership(true);
    setMessage('');
    setError('');
    try {
      await axios.put(API.updateUserProfile(user.id), {
        full_name: membershipForm.full_name || undefined,
        gender: membershipForm.gender || undefined,
        date_of_birth: membershipForm.date_of_birth || undefined,
      }, { headers: authHeaders() });
      setMessage('Membership details saved.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save membership details.');
    } finally {
      setSavingMembership(false);
    }
  };

  const saveCompany = async () => {
    if (!user) return;
    setSavingCompany(true);
    setMessage('');
    setError('');
    try {
      await axios.put(API.updateUserProfile(user.id), {
        company_name: companyForm.company_name || undefined,
        company_representative: companyForm.company_representative || undefined,
        company_register_number: companyForm.company_register_number || undefined,
      }, { headers: authHeaders() });
      setMessage('Company information saved.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save company information.');
    } finally {
      setSavingCompany(false);
    }
  };

  const changePassword = async () => {
    if (!user) return;
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError("New password and confirmation don't match.");
      return;
    }
    setChangingPassword(true);
    setMessage('');
    setError('');
    try {
      await axios.put(API.changePassword(user.id), {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, { headers: authHeaders() });
      setMessage('Password changed.');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to change password.');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!user || loading) return <div>Loading...</div>;

  const sectionTitleStyle = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' } as const;

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: '32px' }}>Profile Details</h1>

      {(message || error) && (
        <div style={{ marginBottom: '24px', padding: '16px', borderRadius: '8px', background: error ? '#FEF2F2' : '#F0FDF4', color: error ? '#991B1B' : '#166534', border: `1px solid ${error ? '#FCA5A5' : '#BBF7D0'}`, fontSize: '14px' }}>
          {error || message}
        </div>
      )}

      <div style={{ display: 'grid', gap: '32px' }}>
        <div className="section-card">
          <div style={sectionTitleStyle}>
            <Lock size={20} className="text-primary" />
            <h3 className="card-title" style={{ margin: 0 }}>Account</h3>
          </div>
          <div className="form-group">
            <label className="form-label">Registered Email Address</label>
            <input className="form-input" value={user.email} disabled />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="current-password-input">Current Password</label>
              <input id="current-password-input" type="password" className="form-input" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="new-password-input">New Password</label>
              <input id="new-password-input" type="password" className="form-input" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="confirm-password-input">Confirm New Password</label>
              <input id="confirm-password-input" type="password" className="form-input" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} />
            </div>
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: '12px' }}
            onClick={changePassword}
            disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
          >
            {changingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </div>

        <div className="section-card">
          <div style={sectionTitleStyle}>
            <UserIcon size={20} className="text-primary" />
            <h3 className="card-title" style={{ margin: 0 }}>Complete Your Membership Details</h3>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="full-name-input">Full Name</label>
            <input id="full-name-input" className="form-input" value={membershipForm.full_name} onChange={(e) => setMembershipForm({ ...membershipForm, full_name: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="gender-input">Gender</label>
              <input id="gender-input" className="form-input" value={membershipForm.gender} onChange={(e) => setMembershipForm({ ...membershipForm, gender: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="birthday-input">Birthday</label>
              <input id="birthday-input" type="date" className="form-input" value={membershipForm.date_of_birth} onChange={(e) => setMembershipForm({ ...membershipForm, date_of_birth: e.target.value })} />
            </div>
          </div>
          <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={saveMembership} disabled={savingMembership}>
            {savingMembership ? 'Saving...' : 'Save'}
          </button>
        </div>

        <div className="section-card">
          <div style={sectionTitleStyle}>
            <Building2 size={20} className="text-primary" />
            <h3 className="card-title" style={{ margin: 0 }}>Company Information (Optional)</h3>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="company-name-input">Company Name</label>
            <input id="company-name-input" className="form-input" value={companyForm.company_name} onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="company-representative-input">Company Representative</label>
            <input id="company-representative-input" className="form-input" value={companyForm.company_representative} onChange={(e) => setCompanyForm({ ...companyForm, company_representative: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="company-register-number-input">Company Register Number</label>
            <input id="company-register-number-input" className="form-input" value={companyForm.company_register_number} onChange={(e) => setCompanyForm({ ...companyForm, company_register_number: e.target.value })} />
          </div>
          <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={saveCompany} disabled={savingCompany}>
            {savingCompany ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetails;
